import React, { useState, useEffect, useMemo } from 'react'
import './digital-vault-statistical-report.css'
import { Container, Grid, useMediaQuery } from '@material-ui/core'
import { GridSelectionHLPure, SimpleLoading, LabelHLPure } from '../../pures';
import { VictoryAxis, VictoryBar, VictoryChart, VictoryLabel, VictoryPie, VictoryTooltip } from 'victory';
import { PieGraphCustomLabel } from '../digital-vault-reports/graphs';
import useDimensions from "react-use-dimensions";
import dashboardColors from '../digital-vault-reports/dashboardColors';

const icons = ['k-i-file-txt', 'k-i-share', 'k-i-redo', 'k-i-download', 'k-i-print', 'k-i-reset', 'k-i-calendar']

const TextCell = (props) => {
    const {
        dataItem,
        col
    } = props;

    const data = [dataItem.name, dataItem.companyName, Math.abs(dataItem.days)];

    return <td className={`digitalVaultStatisticalReportView-col${col}`}>
        <div>
            <p>{data[col - 1]}</p>
        </div>
    </td>
}

const DigitalVaultStatisticalReportView = (props) => {
    let {
        isLoading,
        setSelectedDocumentId,
        allTopDocuments,
        documentActions,
        confidentialDocuments,
        totalAlerts,
        spaceUsed,
        t
    } = props;

    const [selectedOption, setSelectedOption] = useState(null);
    const onOptionClick = (opt) => setSelectedOption(opt);

    useEffect(() => {
        if (documentActions.length === 0) return;
        setSelectedOption(documentActions[0])
    }, [documentActions]);

    const [topDocumentsShown, setTopDocumentsShown] = useState([]);
    useEffect(() => {
        if (!selectedOption) return;
        let topdocs = allTopDocuments.filter(doc => doc.documentActionId === selectedOption.id);
        topdocs.sort((a, b) => a.count < b.count ? 1 : -1);
        setTopDocumentsShown(topdocs)
    }, [selectedOption, allTopDocuments])

    const maxValueDocuments = useMemo(() => Math.max(...topDocumentsShown.map(i => i.count)), [topDocumentsShown]);

    const DocumentsCellOne = (props) => <TextCell {...props} col={1} />
    const DocumentsCellTwo = (props) => <TextCell {...props} col={2} />

    const mobileResolution = useMediaQuery("(max-width:960px)");
    const [barGraphRef, barGraphDims] = useDimensions();

    if (isLoading) return <SimpleLoading />

    return (
        <Container disableGutters className='digitalVaultStatisticalReportView'>
            <Grid container spacing={3}>
                <Grid item xs={12} md={4} className='digitalVaultStatisticalReportView-topDocuments'>
                    <div className='digitalVaultReportsView-card'>
                        <div className='digitalVaultReportsView-top-documents'>    
                            <LabelHLPure text={t("digitalVaultStatisticalReportView.top.documents")} fontSize={'18px'}/>
                            <span class="k-icon k-font-icon k-i-star"></span>
                        </div>
                        <ul className='topDocuments-optionsList'>
                            {
                                documentActions.map((o, i) => <li key={i}>
                                    <button
                                        className={`topDocuments-optionBtn ${o.translatedName === selectedOption.translatedName ? "selected" : ""}`}
                                        onClick={() => onOptionClick(o)} aria-label={o.translatedName}
                                    >
                                        <span class={`k-icon k-font-icon ${icons[i]}`}></span>
                                    </button>
                                </li>)
                            }
                        </ul>
                        <LabelHLPure text={selectedOption?.translatedName} fontSize={'18px'}/>
                        <ul className='topDocuments-documentList'>
                            {
                                topDocumentsShown.map(doc => {
                                    const value = parseInt(doc.count)
                                    let width = `${value / maxValueDocuments * 100}%`
                                    return <li key={doc.id} className='topDocuments-documentList-item'>
                                        <div className='topDocuments-name'>
                                            <LabelHLPure text={doc.name}/>
                                        </div>
                                        <div className='topDocuments-value'>
                                            <LabelHLPure text={value}/>
                                        </div>
                                        <div className='topDocuments-graph'>
                                            <div className='topDocuments-bar-container'>
                                                <div className='topDocuments-bar' style={{ width, fontFamily: 'Quicksand' }}></div>
                                            </div>
                                        </div>
                                    </li>
                                })
                            }
                        </ul>
                    </div>
                </Grid>

                <Grid
                    item
                    xs={12} md={8}
                    className="dashboard-item"
                >
                    <div className="digitalVaultReportsView-card graph-container">
                        <div className="graph pie">
                            <LabelHLPure text={t("digitalVaultStatisticalReportView.number.alerts")} fontSize={'22px'}/>
                            <svg width={"100%"} height={"100%"} viewBox={`0 0 400 300`}>
                                <VictoryLabel
                                    textAnchor="middle"
                                    style={{ fontSize: 20, fontFamily: 'Quicksand' }}
                                    x={200} y={150}
                                    text={`Total: ${totalAlerts.reduce((accumulator, currentValue) => {
                                        return accumulator + currentValue.alerts
                                    }, 0)}`}
                                />
                                <VictoryPie
                                    data={totalAlerts}
                                    standalone={false}
                                    x="companyName"
                                    y="alerts"
                                    padding={40}
                                    height={290}
                                    animate={{
                                        duration: 100
                                    }}
                                    padAngle={2}
                                    innerRadius={75}
                                    colorScale={dashboardColors}

                                    style={{ labels: { fill: "white", fontFamily: 'Quicksand' } }}
                                    labelRadius={120}
                                    labels={({ datum }) => [`${datum.alerts}`, `${t("digitalVaultStatisticalReportView.alerts")}`]}
                                    labelComponent={<PieGraphCustomLabel width={400} height={290} />}
                                />
                            </svg>
                        </div>
                    </div>
                </Grid>

                <Grid item xs={12}>
                    <div className='digitalVaultReportsView-card'>
                        <LabelHLPure text={t("digitalVaultStatisticalReportView.confidential")} fontSize={'18px'}/>
                        <GridSelectionHLPure
                            skip={0}
                            take={5}
                            dataGrid={confidentialDocuments}

                            columnOne
                            titleRow1={t("digitalVaultStatisticalReportView.grid.title.1")}
                            cellColumnOne={DocumentsCellOne}
                            headerColumnOneClass={"digitalVaultStatisticalReportView-col1"}

                            columnTwo
                            titleRow2={t("digitalVaultStatisticalReportView.grid.title.2")}
                            cellColumnTwo={DocumentsCellTwo}
                            headerColumnTwoClass={"digitalVaultStatisticalReportView-col2"}

                            columnFive
                            titleRow5={""}
                            headerColumnFiveClass={"digitalVaultStatisticalReportView-col5"}
                            columnFiveStyleClass={"digitalVaultStatisticalReportView-col5"}
                            iconOneActive
                            labelOneActions={t("digitalVaultStatisticalReportView.grid.btns.view.document")}
                            mobileFunction
                            activeDialog1
                            btnOpenDialog1={(dataItem) => setSelectedDocumentId(dataItem.id)}

                            onSelectedItem={() => { }}
                            setNameComponentUser={() => { }}
                            setShowDetails={() => { }}
                        />
                    </div>
                </Grid>

                <Grid
                    item
                    xs={12}
                    className="dashboard-item"
                    ref={barGraphRef}
                >
                    <div className="digitalVaultReportsView-card graph-container">
                        <div className='digitalVaultReportsView-card-heading'>
                            <LabelHLPure text={t("digitalVaultStatisticalReportView.storage.space.used")} fontSize={'18px'}/>
                            <LabelHLPure text={`${t("digitalVaultStatisticalReportView.total")}${spaceUsed.reduce((accumulator, currentValue) => {
                                return accumulator + currentValue.space
                            }, 0).toFixed(2)} ${t("digitalVaultStatisticalReportView.mb")}`} fontSize={'18px'}/>
                        </div>
                        <div className="graph">
                            <VictoryChart
                                domainPadding={{ x: 20 }}
                                width={barGraphDims.width}
                                height={mobileResolution ? 600 : 300}
                            // containerComponent={
                            //     <VictoryVoronoiContainer
                            //         radius={25}
                            //         labels={({ datum }) => [datum.companyName, `${datum.space.toFixed(2)} MB`]}
                            //         labelComponent={
                            //             <VictoryTooltip constrainToVisibleArea />
                            //         }
                            //     />
                            // }
                            >
                                <VictoryAxis dependentAxis
                                    label={t("digitalVaultStatisticalReportView.graphic.space")}
                                    style={{
                                        grid: { stroke: '#f3f3f3', strokeWidth: 1 },
                                        axis: { stroke: '#e7e7e7' },
                                        axisLabel: { fontSize: 14, padding: 35, fontFamily:'Quicksand' },
                                        tickLabels: { fontFamily:'Quicksand' }
                                    }}
                                />
                                {
                                    spaceUsed.map((item, i) => {
                                        return (
                                            <VictoryAxis dependentAxis
                                                key={i}
                                                label={item.companyName}
                                                orientation={mobileResolution ? 'top' : 'right'}
                                                style={{
                                                    axis: { stroke: '#e7e7e7' },
                                                    tickLabels: { fill: "transparent", stroke: 'transparent', padding: 0, fontFamily: 'Quicksand' },
                                                    axisLabel: { fontSize: 14, padding: 14, fontFamily: 'Quicksand' },
                                                    
                                                }}
                                                axisValue={item.companyName}
                                            />
                                        );
                                    })
                                }
                                <VictoryAxis
                                    style={{
                                        axis: { stroke: '#e7e7e7' },
                                        tickLabels: { fill: "transparent", padding: 0, fontFamily:'Quicksand' },
                                        axisLabel: { fontFamily:'Quicksand' }

                                    }}
                                />

                                <VictoryBar
                                    barWidth={16}
                                    data={spaceUsed}
                                    x="companyName"
                                    y="space"
                                    sortKey="space"
                                    style={{
                                        data: { fill: dashboardColors[0], fontFamily: 'Quicksand' }
                                    }}
                                    horizontal={mobileResolution}
                                    animate={{
                                        duration: 300
                                    }}
                                    labels={({ datum }) => `${datum.space.toFixed(2)} ${t("digitalVaultStatisticalReportView.mb")}`}
                                    labelComponent={<VictoryTooltip />}
                                />
                            </VictoryChart>
                        </div>
                    </div>
                </Grid>
            </Grid>
        </Container>
    )
}

export default DigitalVaultStatisticalReportView